import {useKpiSearch} from '@domain/expert-system'
import {KpiOption} from '@hconnect/common/types'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Autocomplete as MuiAutocomplete,
  Box,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import {memo, useCallback, useMemo} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {KpiEditFormValues, SlaTargetType} from '../../types'

type EditKpiRowProps = {
  tabIndex: number
  groupIndex: number | null // null for ungrouped
  definitionIndex: number
  onDelete: () => void
  plantId: string
}

const TARGET_TYPE_OPTIONS: {value: SlaTargetType; labelKey: string}[] = [
  {value: SlaTargetType.GreaterThan, labelKey: 'editKpisModal.rangeOptions.greaterThan'},
  {value: SlaTargetType.LessThan, labelKey: 'editKpisModal.rangeOptions.lessThan'},
  {value: SlaTargetType.Range, labelKey: 'editKpisModal.rangeOptions.range'}
]

const EditKpiRowComponent = ({
  tabIndex,
  groupIndex,
  definitionIndex,
  onDelete,
  plantId
}: EditKpiRowProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {control, setValue} = useFormContext<KpiEditFormValues>()

  // Build the field path based on whether this is grouped or ungrouped
  const basePath = useMemo(
    () =>
      groupIndex !== null
        ? (`tabGroups.${tabIndex}.groups.${groupIndex}.definitions.${definitionIndex}` as const)
        : (`tabGroups.${tabIndex}.ungroupedDefinitions.${definitionIndex}` as const),
    [tabIndex, groupIndex, definitionIndex]
  )

  // Use useWatch for targeted subscription - only re-renders when this specific field changes
  const targetType = useWatch({control, name: `${basePath}.slaTarget.targetType`})
  const isRangeType = targetType === SlaTargetType.Range

  const sourceKpiId = useWatch({control, name: `${basePath}.sourceKpiId`})

  // Fetch CCE KPIs for the plant
  const {data: kpiResponse, isLoading: isLoadingKpis} = useKpiSearch(plantId)

  const kpiOptions = useMemo<KpiOption[]>(() => {
    if (!kpiResponse?.processStages) return []
    return kpiResponse.processStages
      .flatMap((stage) => stage.definitionsWithResults)
      .map((kpiWithValue) => ({
        id: String(kpiWithValue.definition.kpiAssignment.id),
        label: kpiWithValue.definition.displayName
      }))
  }, [kpiResponse])

  const selectedKpiOption = useMemo<KpiOption | null>(() => {
    if (!sourceKpiId) return null
    return kpiOptions.find((opt) => opt.id === sourceKpiId) ?? null
  }, [sourceKpiId, kpiOptions])

  const handleSourceKpiChange = useCallback(
    (option: KpiOption | null) => {
      setValue(`${basePath}.sourceKpiId`, option?.id ?? null, {shouldDirty: true})
    },
    [setValue, basePath]
  )

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{py: 1, '&:not(:last-child)': {borderBottom: '1px solid', borderColor: 'divider'}}}
    >
      {/* KPI Name */}
      <Box sx={{flex: 2, minWidth: 200}}>
        <Controller
          key={`${basePath}.name`}
          name={`${basePath}.name`}
          control={control}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              size="small"
              fullWidth
              placeholder={t('editKpisModal.columns.kpi')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Source KPI (CCE KPI) */}
      <Box sx={{flex: 2, minWidth: 200}}>
        <MuiAutocomplete<KpiOption, false, false, false>
          options={kpiOptions}
          value={selectedKpiOption}
          onChange={(_event, newValue) => handleSourceKpiChange(newValue)}
          getOptionLabel={(option) => option?.label ?? ''}
          isOptionEqualToValue={(option, val) => option?.id === val?.id}
          loading={isLoadingKpis}
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField {...params} placeholder={t('editKpisModal.columns.sourceKpi')} />
          )}
        />
      </Box>

      {/* Target Value / Range */}
      <Box sx={{flex: 1, minWidth: isRangeType ? 150 : 100}}>
        {isRangeType ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Controller
              key={`${basePath}.slaTarget.minValue`}
              name={`${basePath}.slaTarget.minValue`}
              control={control}
              render={({field, fieldState}) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.onChange(val === '' ? null : Number(val))
                  }}
                  size="small"
                  type="number"
                  placeholder="Min"
                  error={!!fieldState.error}
                  sx={{width: 70}}
                />
              )}
            />
            <Box sx={{px: 0.5}}>-</Box>
            <Controller
              key={`${basePath}.slaTarget.maxValue`}
              name={`${basePath}.slaTarget.maxValue`}
              control={control}
              render={({field, fieldState}) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.onChange(val === '' ? null : Number(val))
                  }}
                  size="small"
                  type="number"
                  placeholder="Max"
                  error={!!fieldState.error}
                  sx={{width: 70}}
                />
              )}
            />
          </Stack>
        ) : (
          <Controller
            key={`${basePath}.slaTarget.targetValue`}
            name={`${basePath}.slaTarget.targetValue`}
            control={control}
            render={({field, fieldState}) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  const val = e.target.value
                  field.onChange(val === '' ? null : Number(val))
                }}
                size="small"
                fullWidth
                type="number"
                placeholder={t('editKpisModal.columns.target')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}
      </Box>

      {/* Unit */}
      <Box sx={{flex: 1, minWidth: 100}}>
        <Controller
          key={`${basePath}.slaTarget.unit`}
          name={`${basePath}.slaTarget.unit`}
          control={control}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              size="small"
              fullWidth
              placeholder={t('editKpisModal.columns.unit')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Range Type Dropdown */}
      <Box sx={{flex: 1, minWidth: 120}}>
        <Controller
          key={`${basePath}.slaTarget.targetType`}
          name={`${basePath}.slaTarget.targetType`}
          control={control}
          render={({field}) => (
            <TextField {...field} select size="small" fullWidth>
              {TARGET_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>

      {/* Delete Button */}
      <Box
        sx={{
          width: 40,
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'center',
          justifyContent: 'center'
        }}
      >
        <Tooltip title={t('editKpisModal.action.deleteDefinition')}>
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
            aria-label={t('editKpisModal.action.deleteDefinition')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  )
}

export const EditKpiRow = memo(EditKpiRowComponent)
